import React, { useState, useEffect } from 'react';
import { Partner, CreatePartnerRequest, UpdatePartnerRequest, PartnerType, PartnerTypeLabels } from '../../types/partners';

interface PartnerFormProps {
  partner?: Partner;
  onSave: (data: CreatePartnerRequest | UpdatePartnerRequest) => void;
  onCancel: () => void;
  loading?: boolean;
}

const PartnerForm: React.FC<PartnerFormProps> = ({
  partner,
  onSave,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState<CreatePartnerRequest>({
    name: '',
    partnerType: PartnerType.Sponsor,
    description: '',
    website: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    logo: '',
    isActive: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (partner) {
      setFormData({
        name: partner.name,
        partnerType: partner.partnerType,
        description: partner.description || '',
        website: partner.website || '',
        contactEmail: partner.contactEmail || '',
        contactPhone: partner.contactPhone || '',
        address: partner.address || '',
        logo: partner.logo || '',
        isActive: partner.isActive
      });
    } else {
      setFormData({
        name: '',
        partnerType: PartnerType.Sponsor,
        description: '',
        website: '',
        contactEmail: '',
        contactPhone: '',
        address: '',
        logo: '',
        isActive: true
      });
    }
    setErrors({});
  }, [partner]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (!formData.partnerType) {
      newErrors.partnerType = 'Typ ist erforderlich';
    }

    if (formData.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Ungültige E-Mail-Adresse';
    }

    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = 'Website muss mit http:// oder https:// beginnen';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSave(formData);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            {partner ? 'Partner bearbeiten' : 'Neuen Partner erstellen'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              disabled={loading}
              style={{
                ...inputStyle,
                ...(errors.name ? errorInputStyle : {})
              }}
              placeholder="Name des Partners"
            />
            {errors.name && <span style={errorTextStyle}>{errors.name}</span>}
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Typ *</label>
            <select
              name="partnerType"
              value={formData.partnerType}
              onChange={handleInputChange}
              disabled={loading}
              style={{
                ...inputStyle,
                ...(errors.partnerType ? errorInputStyle : {})
              }}
            >
              {Object.entries(PartnerTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {errors.partnerType && <span style={errorTextStyle}>{errors.partnerType}</span>}
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Beschreibung</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              disabled={loading}
              style={textareaStyle}
              placeholder="Beschreibung des Partners"
            />
          </div>

          <div style={rowStyle}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Website</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://example.com"
                disabled={loading}
                style={{
                  ...inputStyle,
                  ...(errors.website ? errorInputStyle : {})
                }}
              />
              {errors.website && <span style={errorTextStyle}>{errors.website}</span>}
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>E-Mail</label>
              <input
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleInputChange}
                placeholder="partner@example.com"
                disabled={loading}
                style={{
                  ...inputStyle,
                  ...(errors.contactEmail ? errorInputStyle : {})
                }}
              />
              {errors.contactEmail && <span style={errorTextStyle}>{errors.contactEmail}</span>}
            </div>
          </div>

          <div style={rowStyle}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Telefon</label>
              <input
                type="tel"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleInputChange}
                placeholder="+49 89 123456"
                disabled={loading}
                style={inputStyle}
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Logo URL</label>
              <input
                type="url"
                name="logo"
                value={formData.logo}
                onChange={handleInputChange}
                placeholder="https://example.com/logo.png"
                disabled={loading}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Adresse</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              rows={2}
              disabled={loading}
              style={textareaStyle}
              placeholder="Straße, PLZ Ort"
            />
          </div>

          <div style={checkboxGroupStyle}>
            <input
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
              disabled={loading}
              style={checkboxStyle}
            />
            <label htmlFor="isActive" style={checkboxLabelStyle}>
              Aktiv
            </label>
          </div>

          <div style={buttonGroupStyle}>
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              style={cancelButtonStyle}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              style={submitButtonStyle}
            >
              {loading ? 'Speichern...' : (partner ? 'Aktualisieren' : 'Erstellen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '2rem',
  width: '90%',
  maxWidth: '600px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
};

const headerStyle: React.CSSProperties = {
  marginBottom: '1.5rem',
  paddingBottom: '1rem',
  borderBottom: '1px solid #e5e7eb',
};

const titleStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: '600',
  color: '#1f2937',
  margin: 0,
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
};

const formGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const rowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1rem',
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: '500',
  color: '#374151',
};

const inputStyle: React.CSSProperties = {
  padding: '0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: '0.5rem',
  fontSize: '0.875rem',
  outline: 'none',
  transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: '80px',
};

const errorInputStyle: React.CSSProperties = {
  borderColor: '#ef4444',
};

const errorTextStyle: React.CSSProperties = {
  color: '#ef4444',
  fontSize: '0.75rem',
  marginTop: '0.25rem',
};

const checkboxGroupStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
};

const checkboxStyle: React.CSSProperties = {
  width: '1rem',
  height: '1rem',
};

const checkboxLabelStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  color: '#374151',
  cursor: 'pointer',
};

const buttonGroupStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '0.75rem',
  marginTop: '1.5rem',
  paddingTop: '1rem',
  borderTop: '1px solid #e5e7eb',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  backgroundColor: '#f3f4f6',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: '0.5rem',
  fontSize: '0.875rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.15s ease-in-out',
};

const submitButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  backgroundColor: '#3b82f6',
  color: 'white',
  border: 'none',
  borderRadius: '0.5rem',
  fontSize: '0.875rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.15s ease-in-out',
};

export default PartnerForm;
